import React from 'react';
import { Box, Step, StepButton, StepLabel, Typography } from '@mui/material';
import { ProgressTrackerStages, ProgressTrackerStatus } from 'services/account';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { SECONDARY_COLOR } from 'theme/constant';
import { UIDateFormat } from 'helpers/dateTimeFormat';
import { FormationsPlan } from 'models/account';

const useStyle = makeStyles(() => ({
  root: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid transparent'
  },
  button: {
    textAlign: 'left',
  },
  active: {
    backgroundColor: 'white',
    border: '2px solid #36B73A',
  },
  notStarted: {
    backgroundColor: 'white',
    border: '2px solid #C4C4C4',
  },
  nonActionable: {
    backgroundColor: '#4493FB',
  },
  actionable: {
    backgroundColor: 'orange',
  },
  completed: {
    position: 'relative',
    backgroundColor: SECONDARY_COLOR,
    '&::before': {
      content: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="18" width="18" viewBox="0 0 24 24"><path fill="white" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      position: 'absolute',
      top: '60%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    }
  },
}));

interface FormationsStepProps {
  stageKey: string;
  status: ProgressTrackerStatus;
  index: number;
  label: string;
  setActiveStep: (step: number) => void;
  isTrainer: boolean;
  lastUpdatedTime?: string;
  disabled?: boolean;
  activeStep?: number;
  completedSteps?: number;
  totalSteps?: number;
  typeOfAccount?: FormationsPlan;
}

export const FormationsStep = ({
  stageKey,
  status,
  index,
  label,
  setActiveStep,
  isTrainer,
  lastUpdatedTime = '',
  disabled,
  activeStep,
  completedSteps = 0,
  totalSteps = 0,
  typeOfAccount,
}: FormationsStepProps) => {
  const classes = useStyle();

  let isMaskStepCompleted: string | null = null;

  if (typeOfAccount === FormationsPlan.FullSuite) {
    isMaskStepCompleted = localStorage.getItem('filingDocumentsCompleted');
  } else if (typeOfAccount === FormationsPlan.BIB) {
    isMaskStepCompleted = localStorage.getItem('filingDocumentsCompletedBIB');
  }

  const CustomIcon = (props: any) => {
    const { classes: propClass } = props;
    return <div className={clsx(classes.root, propClass.root)} />;
  };

  const getIdentityVerificationTextAndIcon = () => {
    let statusIcon = '';
    let text = '';
    switch (status) {
      case ProgressTrackerStatus.InProgress:
        if (isTrainer) {
          statusIcon = classes.actionable;
          text = 'Action Needed';
        } else if (
          stageKey === ProgressTrackerStages.UploadIdentityProof ||
          stageKey === ProgressTrackerStages.UploadLastYearTaxStatement ||
          stageKey === ProgressTrackerStages.DocumentOfIncorporation
        ) {
          statusIcon = classes.completed;
          text = 'In Review by Formations';
        } else {
          statusIcon = classes.nonActionable;
          text = 'In Review';
        }
        break;
      case ProgressTrackerStatus.Started:
      case ProgressTrackerStatus.Rejected:
        if (isTrainer) {
          statusIcon = classes.nonActionable;
          text = 'Customer Action Needed';
        } else {
          statusIcon = classes.actionable;
          text = 'Action Needed';
        }
        break;

      case ProgressTrackerStatus.Completed:
        statusIcon = classes.completed;
        text = 'Completed';
        break;

      case ProgressTrackerStatus.NotStarted:
      default:
        statusIcon = classes.notStarted;
        break;
    }

    if (index === activeStep) {
      statusIcon = classes.active;
    }

    return { statusIcon, text };
  }

  const getFilingDocumentsTextAndIcon = () => {
    const isComplete = status === ProgressTrackerStatus.Completed || isMaskStepCompleted === 'true'
    const isActiveStep = index === activeStep;

    let statusIcon = '';
    let text = '';
    if (completedSteps === totalSteps || completedSteps + 1 === totalSteps) {
      if (isActiveStep) {
        statusIcon = classes.active;
      } else {
        statusIcon = isComplete ? classes.completed : classes.notStarted;
      }

      text = 'Incorporation in Progress';

      return { statusIcon, text };
    }

    if (completedSteps + 1 !== totalSteps) {
      if (isActiveStep) {
        statusIcon = classes.active;
      } else {
        statusIcon =
          status === ProgressTrackerStatus.Completed
            ? classes.completed
            : classes.notStarted;
      }

      return { statusIcon, text };
    }

    return { statusIcon, text };
  };

  const getInfoBaseOnStatus = () => {
    switch (stageKey) {
      case ProgressTrackerStages.UploadIdentityProof:
      case ProgressTrackerStages.UploadLastYearTaxStatement:
      case ProgressTrackerStages.DocumentOfIncorporation:
        return getIdentityVerificationTextAndIcon();

      case ProgressTrackerStages.FilingDocuments:
        return getFilingDocumentsTextAndIcon();

      default: {
        let statusIcon = '';
        let text = '';

        switch (status) {
          case ProgressTrackerStatus.InProgress:
            if (isTrainer) {
              statusIcon = classes.actionable;
              text = 'Action Needed';
            } else {
              statusIcon = classes.nonActionable;
              text = 'In Review';
            }
            break;

          case ProgressTrackerStatus.Started:
          case ProgressTrackerStatus.Rejected:
            if (isTrainer) {
              statusIcon = classes.nonActionable;
              text = 'Customer Action Needed';
            } else {
              statusIcon = classes.actionable;
              text = 'Action Needed';
            }
            break;

          case ProgressTrackerStatus.Completed:
            statusIcon = classes.completed;
            text = 'Completed';
            break;

          case ProgressTrackerStatus.NotStarted:
          default:
            statusIcon = classes.notStarted;
            break;
        }

        if (index === activeStep) {
          statusIcon = classes.active;
        }

        return { statusIcon, text };
      }
    }
  };

  const { statusIcon, text } = getInfoBaseOnStatus();

  return (
    <Step
      key={stageKey}
      index={index}
      disabled={disabled === false ? false : undefined}
      data-testid={`formations-step-${stageKey}`}
      sx={{
        width: 'fit-content',
      }}
    >
      <StepButton
        className={classes.button}
        onClick={() => setActiveStep(index)}
        optional={
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: '500' }}
          >
            {text}
          </Typography>
        }
        data-testid={`step-${label}`}
      >
        <StepLabel
          StepIconComponent={CustomIcon}
          StepIconProps={{
            classes: {
              root: statusIcon,
            },
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Typography
              variant={activeStep === index ? 'body2B' : 'body2'}
              sx={{
                color: activeStep === index ? 'text.primary' : 'text.secondary',
                margin: 0,
              }}
            >
              {label}
            </Typography>
            <Typography variant="caption">
              {!!lastUpdatedTime && UIDateFormat(lastUpdatedTime)}
            </Typography>
          </Box>
        </StepLabel>
      </StepButton>
    </Step>
  );
};
